// A date cell that saves once per edit, not once per keystroke. A native <input type="date"> reports every
// keystroke while a date is typed: the year arrives as 0002, 0020, 0202, 2026, and clearing a segment gives
// ''. Sent straight to Smartsheet, each was a write of its own, and they could land out of order, leaving a
// wrong or blank date. This keeps the typing local and saves the value when it's a whole, sensible date
// (after a short pause), when the field is left or Enter is pressed, or when the field goes away.
import { useEffect, useRef, useState } from 'react';

/** a whole date a person meant: YYYY-MM-DD, a real calendar day, year 1900–2199 ('' is a deliberate clear). */
export function isSensibleDate(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const y = Number(m[1]);
  if (y < 1900 || y > 2199) return false;
  const d = new Date(Date.UTC(y, Number(m[2]) - 1, Number(m[3])));
  return d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

const SETTLE_MS = 800;

export function DateField({ value, onCommit, title, className = 'field', id, max }: {
  /** the saved value (YYYY-MM-DD or ''). */
  value: string;
  /** called with the new date, or null for a cleared one, at most once per settled edit. */
  onCommit: (v: string | null) => void;
  title?: string;
  className?: string;
  id?: string;
  /** the latest day it takes (YYYY-MM-DD): a later one typed in isn't saved, and goes back on leaving */
  max?: string;
}) {
  const [draft, setDraft] = useState(value);
  const editing = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ draft: value, saved: value, onCommit, max });
  latest.current.onCommit = onCommit;
  latest.current.max = max;

  // a new saved value (someone else's edit, a refresh) shows unless this field is mid-edit
  useEffect(() => {
    latest.current.saved = value;
    if (!editing.current) { setDraft(value); latest.current.draft = value; }
  }, [value]);

  const commit = () => {
    window.clearTimeout(timer.current);
    const { draft: d, saved, max: last } = latest.current;
    if (d === saved || (d !== '' && (!isSensibleDate(d) || (!!last && d > last)))) return; // unchanged, a half-typed date, or past the last day
    latest.current.saved = d;
    latest.current.onCommit(d || null);
  };
  // a pending edit still saves if the field disappears (a card collapses, the view changes)
  useEffect(() => () => commit(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      className={className}
      type="date"
      value={draft}
      title={title}
      id={id}
      max={max}
      onFocus={() => { editing.current = true; }}
      onChange={(e) => {
        const v = e.target.value;
        setDraft(v);
        latest.current.draft = v;
        window.clearTimeout(timer.current);
        // a whole date (picked from the calendar, or the last digit typed) saves after a short pause
        if (isSensibleDate(v)) timer.current = window.setTimeout(commit, SETTLE_MS);
      }}
      onBlur={() => {
        editing.current = false;
        commit();
        // a half-typed date that never became whole goes back to the saved one
        if (latest.current.draft !== latest.current.saved) { setDraft(latest.current.saved); latest.current.draft = latest.current.saved; }
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
    />
  );
}
